import { useState, useEffect } from "react";
import {
    Container, Box, Paper, Typography, Stack, Button, Divider, Alert, Chip,
    CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import QRCode from "react-qr-code";
import { resetBooking } from "../redux/slices/bookingSlice";
import { resetSearch } from "../redux/slices/searchSlice";
import { getTicketByTicketIdAPI, handleDownloadApi } from "../api/tickets";



import { useSnackbar } from "notistack";
import i18n from "../locales/i18n";
import { fmtDate, fmtTime } from "../utils/format";

function InfoRow({ icon, label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Icon icon={icon} width={16} color="#919EAB" />
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
        </Stack>
    );
}


export default function ConfirmationPage() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { bookingResult, paymentResult } = useSelector((state) => state.booking);
    const { selectedTrip, journeyDate } = useSelector((state) => state.search);

    const [ticketDetails, setTicketDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);

    // Iz `paymentResult.invoice.tickets` (vraća `/reservations/issue`) imamo full ticket objekte.
    // Fallback: ako toga nema, dovuci po `bookingResult.ticketIds`.
    const invoiceTickets = paymentResult?.invoice?.tickets;
    const ticketIdsFromBooking = bookingResult?.ticketIds || [];

    useEffect(() => {
        if (Array.isArray(invoiceTickets) && invoiceTickets.length > 0) {
            setTicketDetails(invoiceTickets);
            setLoading(false);
            return;
        }
        if (ticketIdsFromBooking.length === 0) {
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const details = await Promise.all(
                    ticketIdsFromBooking.map(async (id) => {
                        try {
                            const data = await getTicketByTicketIdAPI(id);
                            return data?.data || data;
                        } catch {
                            return { id };
                        }
                    })
                );
                setTicketDetails(details);
            } finally {
                setLoading(false);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownload = async (ticketId) => {
        setDownloading(ticketId);
        try {
            const response = await handleDownloadApi(ticketId);
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `karta-${ticketId}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Greška pri preuzimanju:", err);
            enqueueSnackbar(i18n.t("common.errorDownload"), { variant: "error" });
        } finally {
            setDownloading(null);
        }
    };

    const totalAmount = bookingResult?.amount;
    const merchantPaymentId = bookingResult?.merchantPaymentId;
    // Odgovor izdavanja je omotač — potpisani broj računa stoji u ugnežđenom
    // `invoice` objektu; ostale putanje su ostavljene zbog starijih odgovora.
    const fiscalInvoiceNumber =
        paymentResult?.invoice?.invoice?.invoiceNumber ||
        paymentResult?.invoice?.invoiceNumber ||
        paymentResult?.invoice?.taxCoreInvoice?.invoiceNumber ||
        null;
    const isCompleted = paymentResult?.status === "COMPLETED";

    if (!bookingResult) {
        return (
            <Box sx={{ minHeight: "calc(100vh - 64px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Stack alignItems="center" spacing={2}>
                    <Typography>{t("confirmation.noReservation")}</Typography>
                    <Button variant="contained" onClick={() => navigate("/")}>{t("confirmation.backHome")}</Button>
                </Stack>
            </Box>
        );
    }

    const handleNewSearch = () => {
        dispatch(resetBooking());
        dispatch(resetSearch());
        navigate("/");
    };

    const tickets = ticketDetails;

    // Helper to extract station name from ticket (full Ticket has nested objects)
    const getDepStation = (t) =>
        t.departureStation?.stationName || t.departureStation?.name || t.departureStationName ||
        selectedTrip?.departureStationName || selectedTrip?.fromStation || "";
    const getArrStation = (t) =>
        t.arrivalStation?.stationName || t.arrivalStation?.name || t.arrivalStationName ||
        selectedTrip?.arrivalStationName || selectedTrip?.toStation || "";
    const isReturnLeg = (t) => t.isReturnTicket === true || t.outboundTicketId != null;
    const getDepTime = (t) =>
        isReturnLeg(t) ? "" : (t.timeFromStation || t.calculatedDepartureTime || t.departureTime || selectedTrip?.departureTime || "");
    const getArrTime = (t) =>
        isReturnLeg(t) ? "" : (t.timeToStation || t.arrivalTime || t.calculatedArrivalTime || selectedTrip?.arrivalTime || "");
    const getTicketTypeName = (t) =>
        t.ticketTypeName || t.ticketType?.name || t.ticketType?.typeName || null;
    const getOperator = (t) =>
        t.operator?.operatorName || t.operatorName || t.trip?.line?.operator?.operatorName ||
        selectedTrip?.operatorName || selectedTrip?.carrierName || null;

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: 6 }}>
            <Container maxWidth="sm">
                <Paper sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
                    {/* Success header */}
                    <Box sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "success.lighter", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                        <Icon icon="mdi:check-circle" width={48} color="#36B37E" />
                    </Box>

                    <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                        {t("confirmation.title")}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        {t("confirmation.successBody")}
                    </Typography>

                    <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                        {t("confirmation.emailAlert")}
                    </Alert>

                    {/* Payment transaction details */}
                    {paymentResult && (
                        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: "grey.100", textAlign: "left" }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                <Icon icon="mdi:credit-card-check" width={20} color="#36B37E" />
                                <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 700 }}>{t("confirmation.transactionDetails")}</Typography>
                            </Stack>
                            <Stack spacing={0.75}>
                                <InfoRow icon="mdi:identifier" label={t("confirmation.merchantPaymentId")} value={merchantPaymentId} />
                                <InfoRow icon="mdi:credit-card" label={t("confirmation.paymentMethod")} value={t("confirmation.paymentMethodCard")} />
                                {typeof totalAmount === "number" && (
                                    <InfoRow icon="mdi:cash" label={t("confirmation.amount")} value={`${totalAmount.toFixed(2)} RSD`} />
                                )}
                                {fiscalInvoiceNumber && (
                                    <InfoRow icon="mdi:receipt-text" label={t("confirmation.fiscalNumber")} value={fiscalInvoiceNumber} />
                                )}
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Icon icon="mdi:check-decagram" width={16} color="#919EAB" />
                                        <Typography variant="body2" color="text.secondary">{t("confirmation.status")}</Typography>
                                    </Stack>
                                    <Chip
                                        label={isCompleted ? t("confirmation.paid") : (paymentResult.status || "—")}
                                        size="small"
                                        sx={{ bgcolor: "success.lighter", color: "success.darker", fontWeight: 700, fontSize: "0.7rem", height: 22 }}
                                    />
                                </Stack>
                            </Stack>
                        </Paper>
                    )}

                    <Divider sx={{ mb: 3 }} />

                    {/* Ticket details */}
                    {loading ? (
                        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        tickets.map((ticket, index) => {
                            const depStation = getDepStation(ticket);
                            const arrStation = getArrStation(ticket);

                            return (
                                <Paper key={ticket.id || index} elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200", textAlign: "left" }}>
                                    {/* Route header */}
                                    {(depStation || arrStation) && (
                                        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                                            <Stack alignItems="center" sx={{ flex: 1 }}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "primary.main", mb: 0.5 }} />
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{depStation}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {fmtTime(getDepTime(ticket))}
                                                </Typography>
                                            </Stack>
                                            <Box sx={{ flex: 1, height: 2, bgcolor: "primary.main", mx: 1 }} />
                                            <Stack alignItems="center" sx={{ flex: 1 }}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "primary.main", mb: 0.5 }} />
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{arrStation}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {fmtTime(getArrTime(ticket))}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    )}

                                    {/* QR za skeniranje na ulazu (bez potrebe za PDF) + istaknut verifikacioni broj */}
                                    {(ticket.forQr || ticket.forQR) && (
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 1.5 }}>
                                            <Box sx={{ p: 1.5, bgcolor: "#fff", borderRadius: 2, border: "1px solid", borderColor: "grey.300" }}>
                                                <QRCode value={String(ticket.forQr || ticket.forQR)} size={156} />
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                                {t("confirmation.scanAtGate")}
                                            </Typography>
                                        </Box>
                                    )}
                                    {ticket.validationCode && (
                                        <Box sx={{ textAlign: "center", py: 1.25, px: 2, mb: 1.5, borderRadius: 2, bgcolor: "rgba(25,118,210,0.06)", border: "1px dashed", borderColor: "primary.main" }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
                                                {t("confirmation.verificationCode")}
                                            </Typography>
                                            <Typography variant="h5" component="div" sx={{ fontWeight: 800, color: "primary.main", letterSpacing: 2 }}>
                                                {ticket.validationCode}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Stack spacing={0.75}>
                                        <InfoRow icon="mdi:ticket-confirmation" label={t("confirmation.ticketNumber")} value={ticket.id} />
                                        <InfoRow icon="mdi:calendar" label={t("confirmation.journeyDate")} value={fmtDate(isReturnLeg(ticket) ? ticket.journeyDate : (ticket.journeyDate || journeyDate))} />
                                        <InfoRow icon="mdi:tag" label={t("confirmation.ticketType")} value={getTicketTypeName(ticket)} />
                                        <InfoRow icon="mdi:seat" label={t("confirmation.seat")} value={ticket.seatReserved || ticket.seat} />
                                        <InfoRow icon="mdi:bus-side" label={t("confirmation.operator")} value={getOperator(ticket)} />
                                        <InfoRow icon="mdi:receipt-text" label={t("confirmation.invoiceNumber")} value={ticket.invoiceNumber} />
                                        {ticket.price && (
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{t("confirmation.price")}</Typography>
                                                <Typography variant="subtitle1" component="span" sx={{ fontWeight: 800, color: "primary.main" }}>
                                                    {Number(ticket.price).toLocaleString("sr-Latn", { minimumFractionDigits: 2 })} RSD
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>

                                    {/* Download button */}
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => handleDownload(ticket.id)}
                                        disabled={downloading === ticket.id}
                                        startIcon={
                                            downloading === ticket.id
                                                ? <CircularProgress size={16} color="inherit" />
                                                : <Icon icon="mdi:download" />
                                        }
                                        sx={{ mt: 2, borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                                    >
                                        {downloading === ticket.id ? t("confirmation.downloading") : t("confirmation.downloadPdf")}
                                    </Button>
                                </Paper>
                            );
                        })
                    )}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleNewSearch}
                            startIcon={<Icon icon="mdi:magnify" />}
                            sx={{ borderRadius: 2 }}
                        >
                            {t("confirmation.newSearch")}
                        </Button>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => navigate("/moje-karte")}
                            startIcon={<Icon icon="mdi:ticket-account" />}
                            sx={{ borderRadius: 2 }}
                        >
                            {t("confirmation.myTickets")}
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}
